import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { FilterChips } from './FilterChips';

/**
 * PageFiltersBar Component
 *
 * Standard filters bar with search input, filters button, and active filter chips
 * Memoized to prevent unnecessary re-renders
 *
 * @param {Object} props
 * @param {string} props.searchValue - Current search input value
 * @param {Function} props.onSearchChange - Search change handler
 * @param {Function} props.onSearchClear - Search clear handler
 * @param {string} [props.searchPlaceholder] - Search placeholder text
 * @param {boolean} [props.showFiltersButton=false] - Whether to show filters button
 * @param {Function} [props.onFiltersClick] - Filters button click handler
 * @param {number} [props.activeFiltersCount=0] - Number of active filters for badge
 * @param {Array} [props.filterChips=[]] - Array of active filter chips
 * @param {string} [props.className] - Additional CSS classes
 *
 * @example
 * <PageFiltersBar
 *   searchValue={searchInput}
 *   onSearchChange={setSearchInput}
 *   onSearchClear={clearSearch}
 *   searchPlaceholder="Search users..."
 *   showFiltersButton={true}
 *   onFiltersClick={openFilters}
 *   activeFiltersCount={2}
 *   filterChips={[
 *     { key: 'status', value: 'Active', onRemove: () => resetFilter('status') }
 *   ]}
 * />
 */
export const PageFiltersBar = memo(function PageFiltersBar({
  searchValue,
  onSearchChange,
  onSearchClear,
  searchPlaceholder,
  showFiltersButton = false,
  onFiltersClick,
  activeFiltersCount = 0,
  filterChips = [],
  className = '',
}) {
  const { t } = useTranslation();

  // Memoize input change handler to prevent recreation
  const handleInputChange = useCallback((e) => {
    onSearchChange(e.target.value);
  }, [onSearchChange]);

  return (
    <div className={className}>
      {/* Filter Chips */}
      {filterChips.length > 0 && (
        <FilterChips chips={filterChips} className="mb-3" />
      )}

      {/* Search Input with Filters Button */}
      <div className="flex gap-3">
        {/* Search Input */}
        <div className="relative flex-1">
          <Icon
            icon="lucide:search"
            className="absolute left-3 top-1/2 -translate-y-1/2 icon-muted size-5"
          />
          <input
            type="text"
            value={searchValue}
            onChange={handleInputChange}
            placeholder={searchPlaceholder || t('common.searchPlaceholder', 'Search...')}
            className="w-full pl-10 pr-10 py-2.5 border border-border-strong rounded-lg focus:ring-2 focus:ring-brand focus:border-brand transition-colors"
          />
          {searchValue && (
            <button
              onClick={onSearchClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 icon-muted hover:text-subtle transition-colors cursor-pointer"
              aria-label={t('common.clearSearch', 'Clear search')}
            >
              <Icon icon="lucide:x" className="size-5" />
            </button>
          )}
        </div>

        {/* Filters Button */}
        {showFiltersButton && (
          <button
            onClick={onFiltersClick}
            className="relative flex items-center gap-2 px-4 py-2.5 bg-white border border-border-strong rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-brand focus:border-brand transition-colors cursor-pointer"
            aria-label={t('common.filters', 'Filters')}
          >
            <Icon icon="lucide:filter" className="size-5 icon-muted" />
            <span className="text-sm font-medium">{t('common.filters', 'Filters')}</span>
            {activeFiltersCount > 0 && (
              <span className="absolute -top-2 -right-2 flex items-center justify-center size-6 rounded-full bg-brand text-white text-xs font-bold">
                {activeFiltersCount}
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
});

PageFiltersBar.propTypes = {
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchClear: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string,
  showFiltersButton: PropTypes.bool,
  onFiltersClick: PropTypes.func,
  activeFiltersCount: PropTypes.number,
  filterChips: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      onRemove: PropTypes.func.isRequired,
    })
  ),
  className: PropTypes.string,
};
